import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import styles from './SettlementModal.module.css';
import { declareResult, voidBet } from '../services/api';

/**
 * SettlementModal – Declare result (2 or 3 outcome) or Void confirmation.
 * Uses Radix UI Dialog for viewport-centered positioning and accessibility.
 * Props: show, onHide, mode ('declare' | 'void'), match (row data), onSubmit
 */
const SettlementModal = ({ show, onHide, mode, match, onSubmit }) => {
  const counts = match?.counts ?? 2;
  const totalBets = match?.totalBets ?? 0;
  const isThreeWay = counts >= 3 || totalBets >= 3;
  const teamOne = match?.teamOne ?? 'Team 1';
  const teamTwo = match?.teamTwo ?? 'Team 2';
  const runners = match?.runners ?? [teamOne, teamTwo];
  const matchTitle = match?.matchTitle || `${teamOne} vs ${teamTwo}`;

  const [selectedOutcome, setSelectedOutcome] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (show) {
      setSelectedOutcome(null);
      setSubmitting(false);
    }
  }, [show, mode]);

  const handleSubmit = async () => {
    if (mode === 'declare' && selectedOutcome == null) return;
    setSubmitting(true);
    try {
      if (mode === 'void') {

        const payload = {
          eventid: match.eventId,
          match_id: match.matchId,
          market_type: match.marketType,
          gametype: match.gameType || 'FAN',
        };
        await voidBet(payload);
        await onSubmit?.({ success: true });
      } else {
        const wName = runners[selectedOutcome] ?? '';

        const payload = {
          eventid: match.eventId,
          match_id: match.matchId,
          match_title: matchTitle,
          game_type: match.gameType,
          market_type: match.marketType,
          winnerName: wName
        };

        await declareResult(payload);
        await onSubmit?.({ success: true });
      }
      onHide?.();
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.error || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const title = mode === 'void' ? 'Void Match' : 'Declare Result';
  const selectedDisplay = selectedOutcome != null ? runners[selectedOutcome] : null;

  const canSubmit = mode === 'void' || selectedOutcome != null;
  const isSubmitDisabled = !canSubmit || submitting;

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (isSubmitDisabled) return;
    handleSubmit();
  };

  return (
    <Dialog.Root open={show} onOpenChange={(open) => { if (!open) onHide?.(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.radixOverlay} />
        <Dialog.Content className={styles.radixContent} aria-describedby={undefined}>
          <div style={{ background: 'transparent', border: 0 }}>
            <header className={`modal-header ${styles.modalHeader}`}>
              <Dialog.Title className={styles.modalTitle}>{title}</Dialog.Title>
              <Dialog.Close className={`close ${styles.close}`} aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </Dialog.Close>
            </header>
            <form onSubmit={handleFormSubmit} className={styles.formWrap}>
              <div className={`modal-body ${styles.modalBody}`}>
                <div className={styles.contentTitle}>{matchTitle}</div>

                {mode === 'declare' && (
                  <>
                    {isThreeWay ? (
                      <>
                        <div className={styles.outcomeRow}>
                          {runners.map((runner, idx) => (
                            <div
                              key={idx}
                              className={`${styles.outcomeBox} ${selectedOutcome === idx ? styles.selected : ''}`}
                              onClick={() => setSelectedOutcome(idx)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => e.key === 'Enter' && setSelectedOutcome(idx)}
                            >
                              {runner}
                            </div>
                          ))}
                        </div>
                        <div className={styles.selectedLabel}>
                          {selectedDisplay != null ? (
                            <>Result: <span className={styles.selectedValue}>{selectedDisplay}</span></>
                          ) : (
                            <span className={styles.selectedHint}>Select Team 1, Draw, or Team 2</span>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className={styles.outcomeRow}>
                          {runners.map((runner, idx) => (
                            <React.Fragment key={idx}>
                              {idx > 0 && <span className={styles.vsLabel}>VS</span>}
                              <div
                                className={`${styles.outcomeBox} ${selectedOutcome === idx ? styles.selected : ''}`}
                                onClick={() => setSelectedOutcome(idx)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => e.key === 'Enter' && setSelectedOutcome(idx)}
                              >
                                {runner}
                              </div>
                            </React.Fragment>
                          ))}
                        </div>
                        <div className={styles.selectedLabel}>
                          {selectedDisplay != null ? (
                            <>Result: <span className={styles.selectedValue}>{selectedDisplay}</span></>
                          ) : (
                            <span className={styles.selectedHint}>Select winner</span>
                          )}
                        </div>
                      </>
                    )}
                  </>
                )}

                {mode === 'void' && (
                  <div className={styles.voidMessage}>
                    Do you really want to cancel or refund the bets? All stakes will be refunded to the users. This action cannot be undone.
                  </div>
                )}

                <div className={styles.submitRow}>
                  <button
                    type="button"
                    className={`${styles.submitBtn} ${styles.submitBtnSecondary}`}
                    onClick={onHide}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`${styles.submitBtn} ${mode === 'void' ? styles.submitBtnDanger : styles.submitBtnPrimary}`}
                    disabled={isSubmitDisabled}
                  >
                    {submitting ? 'Submitting...' : mode === 'void' ? 'Confirm Void' : 'Submit'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default SettlementModal;
